import type { RoleOut } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, ErrorState, Icon, TableSkeletonRows } from "@sagot/ui";
import { useWorkspaceContext } from "../session/workspace-context";
import { ROLE_MATRIX_COLUMNS, ROLE_MATRIX_ROWS, roleMatrix } from "./team-model";

interface RolesOverviewCardProps {
  roles: readonly RoleOut[] | undefined;
  isError: boolean;
  onRetry: () => void;
}

/** "Roles & permissions" overview: built-in roles × key permissions; edits live on the Roles page. */
export function RolesOverviewCard({ roles, isError, onRetry }: RolesOverviewCardProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const matrix = roles ? roleMatrix(roles) : null;
  return (
    <section className="card overflow-hidden xl:col-span-2" aria-labelledby="roles-card-title">
      <div className="card-header">
        <div>
          <h2 id="roles-card-title" className="card-title">
            {t("team.rolesCard.title")}
          </h2>
          <div className="mt-0.5 text-xs text-fg-subtle">{t("team.rolesCard.subtitle")}</div>
        </div>
        <Button asChild variant="ghost" size="sm">
          <AppLink href={consoleRoutes.roles(summary.slug)}>
            <Icon name="shield-check" />
            {t("team.rolesCard.manage")}
          </AppLink>
        </Button>
      </div>
      {isError ? (
        <ErrorState headingLevel={3} title={t("team.rolesCard.loadError")} onRetry={onRetry} retryLabel={t("common.retry")} />
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t("team.rolesCard.permission")}</th>
                {(matrix?.columns ?? []).map((role) => (
                  <th key={role.id} className="text-center">
                    {role.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!matrix ? (
                <TableSkeletonRows rows={ROLE_MATRIX_ROWS.length} columns={ROLE_MATRIX_COLUMNS.length + 1} />
              ) : (
                ROLE_MATRIX_ROWS.map((permission) => (
                  <tr key={permission}>
                    <th scope="row" className="text-left font-semibold">
                      {t(`team.rolesCard.rows.${permission.replace(".", "_")}`)}
                    </th>
                    {matrix.columns.map((role) => {
                      const allowed = matrix.grants(permission, role);
                      return (
                        <td key={role.id} className="text-center">
                          <Icon
                            name={allowed ? "check" : "x"}
                            className={allowed ? "inline size-4 text-success" : "inline size-4 text-fg-subtle"}
                            label={allowed ? t("team.rolesCard.allowed") : t("team.rolesCard.notAllowed")}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
